import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { CLASSES, SCHEDULE, FALLBACK_BRANCHES } from "@/data/site";

const CATEGORIES = ["All", "General", "Weight Mgmt", "Strength", "Functional", "Cardio"];

function Intensity({ level }) {
  return (
    <span className="flex items-center gap-1" aria-label={`Intensity ${level} of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={`h-1.5 w-4 ${n <= level ? "bg-gold" : "bg-white/15"}`} />
      ))}
    </span>
  );
}

export default function Classes() {
  const [category, setCategory] = useState("All");
  const [branchFilter, setBranchFilter] = useState("all");

  const filtered = category === "All" ? CLASSES : CLASSES.filter((c) => c.category === category);
  const schedule = SCHEDULE.filter((s) => branchFilter === "all" || s.branch === branchFilter);

  return (
    <main className="pt-28 md:pt-36">
      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-4">Classes & Programs</p>
          <h1 className="font-display uppercase text-4xl sm:text-6xl lg:text-7xl leading-[0.95]">
            Pick your <span className="text-gold-gradient">battle.</span>
          </h1>
        </Reveal>

        <Reveal delay={0.1} className="mt-10 flex flex-wrap gap-3">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`class-filter-${c.toLowerCase()}`}
              onClick={() => setCategory(c)}
              className={`px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] border transition-colors duration-300 ${
                category === c
                  ? "bg-gold text-ink border-gold font-semibold"
                  : "border-white/15 text-white/60 hover:border-gold hover:text-gold"
              }`}
            >
              {c}
            </button>
          ))}
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-14 md:py-20">
        <motion.div layout className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <AnimatePresence mode="popLayout">
            {filtered.map((c) => (
              <motion.article
                layout
                key={c.id}
                data-testid={`class-card-${c.id}`}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.35 }}
                className="group overflow-hidden clip-frame border border-white/10 bg-panel h-full flex flex-col"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={c.image}
                    alt={c.name}
                    className="h-full w-full object-cover opacity-70 transition-all duration-700 group-hover:scale-105 group-hover:opacity-90"
                  />
                  <span className="absolute top-4 left-4 bg-ink/80 glass px-3 py-1 font-mono text-[9px] uppercase tracking-[0.25em] text-gold">
                    {c.category}
                  </span>
                </div>
                <div className="flex flex-col flex-1 p-6">
                  <h3 className="font-display uppercase text-xl">{c.name}</h3>
                  <p className="mt-2 text-xs text-white/55 leading-relaxed flex-1">{c.desc}</p>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{c.duration}</span>
                    <Intensity level={c.intensity} />
                  </div>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 pb-24 md:pb-32">
        <SectionHeading eyebrow="Weekly Timetable" title="Show up on time." />
        <Reveal className="flex flex-wrap gap-3 mb-8">
          <button
            data-testid="schedule-filter-all"
            onClick={() => setBranchFilter("all")}
            className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] border transition-colors ${
              branchFilter === "all" ? "bg-gold text-ink border-gold font-semibold" : "border-white/15 text-white/60 hover:border-gold hover:text-gold"
            }`}
          >
            All Branches
          </button>
          {FALLBACK_BRANCHES.map((b) => (
            <button
              key={b.slug}
              data-testid={`schedule-filter-${b.slug}`}
              onClick={() => setBranchFilter(b.slug)}
              className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] border transition-colors ${
                branchFilter === b.slug ? "bg-gold text-ink border-gold font-semibold" : "border-white/15 text-white/60 hover:border-gold hover:text-gold"
              }`}
            >
              {b.name}
            </button>
          ))}
        </Reveal>
        <Reveal>
          <div className="overflow-x-auto clip-frame border border-white/10">
            <table data-testid="schedule-table" className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="bg-panel2 text-left font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                  <th className="px-6 py-4">Day</th>
                  <th className="px-6 py-4">Time</th>
                  <th className="px-6 py-4">Class</th>
                  <th className="px-6 py-4">Branch</th>
                </tr>
              </thead>
              <tbody>
                {schedule.map((s, i) => {
                  const branch = FALLBACK_BRANCHES.find((b) => b.slug === s.branch);
                  return (
                    <tr key={i} className="border-t border-white/5 hover:bg-panel transition-colors">
                      <td className="px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white/50">{s.day}</td>
                      <td className="px-6 py-4 font-mono text-xs text-white/70">{s.time}</td>
                      <td className="px-6 py-4 font-semibold">{s.class}</td>
                      <td className="px-6 py-4 text-white/60 text-xs">{branch?.name}</td>
                    </tr>
                  );
                })}
                {schedule.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-white/40 text-sm">
                      No classes scheduled for this branch yet — check back soon.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-white/30">
            Sample timetable — confirm live slots at the branch desk.
          </p>
        </Reveal>
        <Reveal delay={0.1} className="mt-12">
          <Link
            data-testid="classes-cta-enquiry"
            to="/contact"
            className="inline-block bg-gold text-ink px-8 py-4 font-bold uppercase text-sm tracking-[0.15em] clip-frame-sm hover:bg-gold-light transition-colors duration-300"
          >
            Book A Class Enquiry
          </Link>
        </Reveal>
      </section>
    </main>
  );
}
